import { observer } from 'mobx-react';
import ApplyViewModel from './ApplyViewModel';
import { Steps } from './ApplyModels';
import { useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { Tag } from 'primereact/tag';
import Helper from '../../../../core/services/Helper';
import { Button, UploadUtility } from '../../../../core/components/Index';
import { Appl } from '../../../../core/Appl';
import { DocumentsModel } from '../../../../core/components/UploadUtility';
import { PersonalStatus, SupportDocs } from '../OraEnums';

const UploadDocuments = observer(() => {

  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');

  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.UploadDocuments, memberId!);
  }, []);

  const getHelpText = (docType: string) => {

    let message = `<p>${docType}</p>`
    if (docType.includes(SupportDocs.DirectDepositAuthorizationVoidedCheck)
      || docType.includes(SupportDocs.CertifiedBankLetterOrSavingsAccountStatement)) {
      message = `<p>To ensure the accuracy of your direct deposit setup and to mitigate the risk of fraud, SCERS requires documentation to verify your bank account information. The required document depends on the type of account you've selected:</p>
      <p class='mt-4'>
      <ul class='list-disc pl-6'>
      <li>For Checking Accounts: You must upload a voided check from your account. This helps confirm the correct account and routing numbers for your financial institution.</li>
      <li>For Savings Accounts: You must upload a certified bank letter or a recent savings account statement that includes your name, account number, and routing number.</li>
      </ul>
      </p>
      <p class='mt-4'>Submitting the correct documentation helps prevent errors and ensures that your retirement payments are deposited securely and without delay.</p>`;
    }
    else if (docType.includes(SupportDocs.BirthCertificateSpouse)
      || docType.includes(SupportDocs.BirthCertificatePartner)
      || docType.includes(SupportDocs.BeneficiariesBirthCertificates)
      || docType.includes('Birth Certificate')) {
      message = `<p>SCERS requires a birth certificate to verify the age of any individual who may receive a continuing monthly benefit after your death. This includes a spouse, domestic partner, or other designated beneficiaries.</p>
      <p class='mt-4'>
      This document helps SCERS:<br/><br/>
      <ul class='list-disc pl-6'>
      <li>Confirm Age for Eligibility & Benefit Calculations: Your benefit and continuance amount are based in part on the age of your beneficiary.</li>
      <li>Ensure Compliance: Verifies beneficiary eligibility and supports legal and actuarial requirements under California law, federal regulations, and IRS rules that govern age and relationship restrictions for continuing benefits.</li>
      </ul>
      </p>
      <p class='mt-4'>Providing this document ensures your benefit is calculated accurately.</p>`;
    }
    else if (docType.includes(SupportDocs.MarriageCertificate)) {
      message = `<p>To verify your eligibility for certain retirement benefit options, SCERS requires proof of your marital status.</p>
      <p class='mt-4'>
      A marriage certificate helps SCERS:<br/><br/>
      <ul class='list-disc pl-6'>
      <li>Determine Benefit Eligibility: Some retirement options are only available to qualified spouses.</li>
      <li>Ensure Legal Compliance: Serves as formal evidence of your marriage under California law.</li>
      <li>Prevent Delays or Disputes: Confirms your spouse's status for continuance or lump-sum beneficiary benefits.</li>
      </ul>
      </p>
      <p class='mt-4'>Providing this document helps SCERS administer your retirement benefits accurately and efficiently.</p>`;
    }
    else if (docType.includes(SupportDocs.CertificateOfDomesticPartnership)) {
      message = `<p>SCERS must verify your legal relationship if you are in a registered domestic partnership. A certificate of domestic partnership provides the necessary proof.</p>
      <p class='mt-4'>
      This document helps SCERS:<br/><br/>
      <ul class='list-disc pl-6'>
      <li>Establish Legal Relationship: Confirms your domestic partnership is recognized under California law.</li>
      <li>Determine Benefit Eligibility: Certain benefit options and beneficiary designations require legal verification.</li>
      <li>Support Proper Benefit Administration: Ensures your partner is correctly recorded for retirement benefit purposes.</li>
      </ul>
      </p>
      <p class='mt-4'>Submitting this document ensures your retirement selections align with eligibility requirements and are processed without delay.</p>`;
    }
    else if (docType.includes(SupportDocs.TemporaryAnnuitiesEstimateFromSocialSecurityOffice)) {
      message = `<p>If you elect the Temporary Annuity option, SCERS must calculate a temporary increase to your benefit that ends at age 62. To do this, we need your estimated Social Security benefit at age 62, as provided by the Social Security Administration.</p>
      <p class='mt-4'>
      This document helps SCERS:<br/><br/>
      <ul class='list-disc pl-6'>
      <li>Calculate the Temporary Annuity Accurately: Your temporary SCERS benefit is based on the projected amount of your Social Security benefit at age 62.</li>
      <li>Ensure Long-Term Accuracy: The estimate ensures the correct reduction is applied to your SCERS benefit after age 62.</li>
      </ul>
      </p>
      <p class='mt-4'>Your Social Security estimate must reflect your projected benefit at exactly age 62, not your full retirement age or other benchmarks.</p>`;
    }
    return (message);
  };

  return (
    <>
      <div className="space-y-6 max-w-4xl">
        <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Upload Documents</h1>
        <p className="text-black text-15px tracking--1percent leading-26px">
          To finalize your retirement application, please upload the required documents below. You can submit your application now without uploading these documents, but remember: <b>your application won't be processed until we receive all required documents.</b>
        </p>
        {
          (vm.model.personalInformation.lovPersonalStatusId === PersonalStatus.Divorced
            || vm.model.personalInformation.lovPersonalStatusId === PersonalStatus.TerminatedDomesticPartnership) &&
          <div className="gap-4 p-6 border-[2px] border-[#D82042] bg-[#d8204212] flex rounded-md mb-4" >
            <div><svg viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" className='w-7 h-7'>
              <path d="M12.5 25.5C15.8152 25.5 18.9946 24.183 21.3388 21.8388C23.683 19.4946 25 16.3152 25 13C25 9.68479 23.683 6.50537 21.3388 4.16117C18.9946 1.81696 15.8152 0.5 12.5 0.5C9.18479 0.5 6.00537 1.81696 3.66117 4.16117C1.31696 6.50537 0 9.68479 0 13C0 16.3152 1.31696 19.4946 3.66117 21.8388C6.00537 24.183 9.18479 25.5 12.5 25.5ZM12.5 6.75C13.1494 6.75 13.6719 7.27246 13.6719 7.92188V13.3906C13.6719 14.04 13.1494 14.5625 12.5 14.5625C11.8506 14.5625 11.3281 14.04 11.3281 13.3906V7.92188C11.3281 7.27246 11.8506 6.75 12.5 6.75ZM10.9375 17.6875C10.9375 17.2731 11.1021 16.8757 11.3951 16.5826C11.6882 16.2896 12.0856 16.125 12.5 16.125C12.9144 16.125 13.3118 16.2896 13.6049 16.5826C13.8979 16.8757 14.0625 17.2731 14.0625 17.6875C14.0625 18.1019 13.8979 18.4993 13.6049 18.7924C13.3118 19.0854 12.9144 19.25 12.5 19.25C12.0856 19.25 11.6882 19.0854 11.3951 18.7924C11.1021 18.4993 10.9375 18.1019 10.9375 17.6875Z" fill="#D82042" />
            </svg>
            </div>
            <div className='-mt-1'>
              <div className='text-[#D82042] text-22px font-semibold mb-1 tracking--1percent'>Reminder: Community Property Documentation</div>
              <p className="text-black text-15px tracking--1_5percent">
                Because you indicated you are Divorced or your Domestic Partnership was Terminated, please upload documents related to your former marriage or partnership (e.g. Judgment of Dissolution, Marital Settlement Agreement, etc.) using the <b>+ ADD DOCUMENT</b> option below.<br /><br />Please note that SCERS may contact you if additional documentation is needed, and your retirement application cannot be fully processed until all required documents have been received.
              </p>
            </div>
          </div>
        }
        <div className="space-y-2">
          {vm.requiredDocuments.map((doc: DocumentsModel) => (
            <div key={doc.docId} className="flex md:justify-between md:items-center border-b py-1 md:py-4 w-full flex-col md:flex-row gap-4">
              <div className="flex-1 text-15px text-black font-normal   ">
                {doc.isNew ? (doc.docTypeId === 1000 ? `Member Provided - ${doc.docType}` : doc.docType)
                  : (<a

                    onClick={async (e) => {
                      e.preventDefault();
                      await vm.downloadDocumentAsync(doc.docId!, doc.docFilename!);
                    }}
                    className='text-primary-500 text-15px underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 rounded-sm px-0.5'
                    tabIndex={0}
                    role="button"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        await vm.downloadDocumentAsync(doc.docId!, doc.docFilename!);
                      }
                    }}
                  >{doc.docTypeId === 1000 ? `Member Provided - ${doc.docType}` : doc.docType}</a>)}
                {doc.isNew && <button
                  className="inline ml-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-600 focus:ring-offset-1 rounded-full p-0.5"
                  onClick={() => Appl.MessageBox.show(getHelpText(doc.docType!), 'Why Is This Document Required?', '', "Close", true)}
                  tabIndex={0}
                  role="button"
                  aria-label="Why is this document required?"
                >
                  <svg className='size-3' viewBox="0 0 12 13" fill="none">
                    <path d="M6 1.625C7.29293 1.625 8.53291 2.13861 9.44715 3.05285C10.3614 3.96709 10.875 5.20707 10.875 6.5C10.875 7.79293 10.3614 9.03291 9.44715 9.94715C8.53291 10.8614 7.29293 11.375 6 11.375C4.70707 11.375 3.46709 10.8614 2.55285 9.94715C1.63861 9.03291 1.125 7.79293 1.125 6.5C1.125 5.20707 1.63861 3.96709 2.55285 3.05285C3.46709 2.13861 4.70707 1.625 6 1.625ZM6 12.5C7.5913 12.5 9.11742 11.8679 10.2426 10.7426C11.3679 9.61742 12 8.0913 12 6.5C12 4.9087 11.3679 3.38258 10.2426 2.25736C9.11742 1.13214 7.5913 0.5 6 0.5C4.4087 0.5 2.88258 1.13214 1.75736 2.25736C0.632141 3.38258 0 4.9087 0 6.5C0 8.0913 0.632141 9.61742 1.75736 10.7426C2.88258 11.8679 4.4087 12.5 6 12.5ZM5.0625 8.375C4.75078 8.375 4.5 8.62578 4.5 8.9375C4.5 9.24922 4.75078 9.5 5.0625 9.5H6.9375C7.24922 9.5 7.5 9.24922 7.5 8.9375C7.5 8.62578 7.24922 8.375 6.9375 8.375H6.75V6.3125C6.75 6.00078 6.49922 5.75 6.1875 5.75H5.0625C4.75078 5.75 4.5 6.00078 4.5 6.3125C4.5 6.62422 4.75078 6.875 5.0625 6.875H5.625V8.375H5.0625ZM6 5C6.19891 5 6.38968 4.92098 6.53033 4.78033C6.67098 4.63968 6.75 4.44891 6.75 4.25C6.75 4.05109 6.67098 3.86032 6.53033 3.71967C6.38968 3.57902 6.19891 3.5 6 3.5C5.80109 3.5 5.61032 3.57902 5.46967 3.71967C5.32902 3.86032 5.25 4.05109 5.25 4.25C5.25 4.44891 5.32902 4.63968 5.46967 4.78033C5.61032 4.92098 5.80109 5 6 5Z" fill="#557122" />
                  </svg>
                </button>
                }
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  {!doc.isNew ? (
                    <>
                      <div className="flex justify-stretch" >
                        <Tag severity="success" value="Upload Complete" className="!rounded-r-none" />
                        <div className="border-2 border-primary-500 rounded-r-md px-1 flex items-center">
                          <svg width="11" height="9" viewBox="0 0 11 9" fill="none" className="w-3 h-3">
                            <path d="M10.7698 0.734338C11.0767 1.04679 11.0767 1.55421 10.7698 1.86666L4.48482 8.26566C4.17794 8.57811 3.67956 8.57811 3.37267 8.26566L0.230164 5.06616C-0.0767213 4.75371 -0.0767213 4.24629 0.230164 3.93384C0.537049 3.62139 1.03543 3.62139 1.34232 3.93384L3.92997 6.56593L9.66014 0.734338C9.96702 0.421887 10.4654 0.421887 10.7723 0.734338H10.7698Z" fill="#557122" />
                          </svg>
                        </div>
                      </div>

                      <a
                        style={{ cursor: 'pointer' }}
                        role="button" tabIndex={0} className="ml-2 flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 rounded-md"

                        onClick={async (e) => {
                          e.preventDefault();
                          Appl.ConfirmBox.show('If you proceed, this document will be removed from your retirement application.', 'Are You Sure You Wish to Delete', '', "Yes, I am sure", "Cancel",
                            async () => await vm.onRemoveDocumentAsync(doc.docId!), undefined, true)
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            Appl.ConfirmBox.show('If you proceed, this document will be removed from your retirement application.', 'Are You Sure You Wish to Delete', '', "Yes, I am sure", "Cancel",
                              async () => await vm.onRemoveDocumentAsync(doc.docId!), undefined, true)
                          }
                        }}


                      >
                        <svg
                          width="14"
                          height="16"
                          viewBox="0 0 14 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className=" w-4 h-4 transition-colors duration-200"
                          style={{ transition: "fill 0.2s ease-in-out" }}
                          onMouseEnter={(e) => (e.currentTarget.querySelector('path')!.style.fill = "#D82042")}
                          onMouseLeave={(e) => (e.currentTarget.querySelector('path')!.style.fill = "#D82042")}
                        >
                          <path d="M5.11875 0H8.88125C9.25938 0 9.60625 0.2125 9.775 0.553125L10 1H13C13.5531 1 14 1.44687 14 2C14 2.55312 13.5531 3 13 3H1C0.446875 3 0 2.55312 0 2C0 1.44687 0.446875 1 1 1H4L4.225 0.553125C4.39375 0.2125 4.74062 0 5.11875 0ZM1 4H13L12.3375 14.5938C12.2875 15.3844 11.6312 16 10.8406 16H3.15937C2.36875 16 1.7125 15.3844 1.6625 14.5938L1 4ZM4.46875 7.46875C4.175 7.7625 4.175 8.2375 4.46875 8.52812L5.9375 9.99687L4.46875 11.4656C4.175 11.7594 4.175 12.2344 4.46875 12.525C4.7625 12.8156 5.2375 12.8187 5.52812 12.525L6.99687 11.0562L8.46562 12.525C8.75937 12.8187 9.23438 12.8187 9.525 12.525C9.81562 12.2312 9.81875 11.7562 9.525 11.4656L8.05625 9.99687L9.525 8.52812C9.81875 8.23438 9.81875 7.75938 9.525 7.46875C9.23125 7.17812 8.75625 7.175 8.46562 7.46875L6.99687 8.9375L5.52812 7.46875C5.23438 7.175 4.75938 7.175 4.46875 7.46875Z"
                            fill="#D82042"
                          />
                        </svg>
                      </a>
                    </>
                  ) : (

                    <>


                      <a role="button" tabIndex={0} className="flex justify-stretch cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2 rounded-md"
                        onClick={async (e) => {
                          e.preventDefault();
                          await vm.showUploadDialogAsync(doc);
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            await vm.showUploadDialogAsync(doc);
                          }
                        }}>
                        <Tag severity="warning" value="Upload Required" className="!rounded-r-none" />
                        <div className="border-2 border-orange-500 rounded-r-md px-1 flex items-center">
                          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" className="w-3 h-3">
                            <path d="M5.4996 0.5C5.8047 0.5 6.08617 0.661144 6.24087 0.925421L10.8818 8.83224C11.0387 9.09866 11.0387 9.4274 10.8861 9.69382C10.7336 9.96025 10.4478 10.1257 10.1406 10.1257H0.858643C0.551395 10.1257 0.265632 9.96025 0.113082 9.69382C-0.0394677 9.4274 -0.0373191 9.09651 0.117379 8.83224L4.75834 0.925421C4.91304 0.661144 5.1945 0.5 5.4996 0.5ZM5.4996 3.2502C5.21384 3.2502 4.98394 3.4801 4.98394 3.76586V6.17228C4.98394 6.45804 5.21384 6.68794 5.4996 6.68794C5.78536 6.68794 6.01526 6.45804 6.01526 6.17228V3.76586C6.01526 3.4801 5.78536 3.2502 5.4996 3.2502ZM6.18715 8.06304C6.18715 7.88069 6.11471 7.70581 5.98577 7.57687C5.85683 7.44793 5.68195 7.37549 5.4996 7.37549C5.31725 7.37549 5.14237 7.44793 5.01343 7.57687C4.88449 7.70581 4.81205 7.88069 4.81205 8.06304C4.81205 8.24539 4.88449 8.42027 5.01343 8.54921C5.14237 8.67815 5.31725 8.75059 5.4996 8.75059C5.68195 8.75059 5.85683 8.67815 5.98577 8.54921C6.11471 8.42027 6.18715 8.24539 6.18715 8.06304Z" fill="#CF7119" />
                          </svg>
                        </div>

                      </a> <div className="ml-2 w-4 h-4 transition-colors duration-200" /></>
                  )}
                </div>
              </div>
              <div>
              </div>
            </div>
          ))}
          <div className='flex justify-stretch'>
            <div className="flex-1"></div>
            <div>
              <button onClick={async () => await vm.showUploadDialogAsync(undefined)}//Other
                className="text-primary-500 text-sm underline text-right">
                <span className='flex font-bold'>
                  {/* <Plus className='bg-green-50 me-2' /> */}
                  + ADD DOCUMENT</span>
              </button>
            </div>
          </div>
        </div>
        <div className="flex space-x-4 mt-6">
          <Button
            label="Save & Continue" rounded
            onClick={async () => await vm.onSubmitRequiredDocumentsAsync()}
          />
        </div>
        <UploadUtility
          visible={vm.uploadDialogVisible}
          dialogHeader={'Upload Document'}
          onHide={async () => await vm.hideUploadDialogAsync()}
          onOk={async () => await vm.onUploadDocumentsAsync()}
          uploadComplete={vm.documentUploadCompleted}
          activeDocument={vm.activeDocument}
          maxFileSize={Appl.Setting.AllowedFileSizeBytes}
          chooseLabel="Browse Files"
          docTypeId={vm.activeDocument.docTypeId}
          allowedFileExtensions={Appl.Setting.AllowedFileExtensions}
          readonlyDocNameInput={vm.activeDocument.docTypeId !== 1000}//Others
          isbusy={vm.documentUploading}
          emptyTemplateInfoText={`Choose ${Appl.Setting.AllowedFileExtensions} formats, up to ${Helper.formatBytes(Appl.Setting.AllowedFileSizeBytes)}`}
        />
      </div>
    </>
  );
});

export default UploadDocuments;
